import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { forkJoin, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { CredentialingService } from 'src/app/credentialing/credentialing.service';
import { AutoAssignType, SETTING_IDS } from 'src/app/credentialing/working-list/models/auto-assign-type.model';
import {
    loadAgencies,
    loadAgenciesFailure,
    loadAgenciesSuccess,
    loadAutoAssignSettings,
    loadAutoAssignSettingsFailure,
    loadAutoAssignSettingsSuccess,
    loadCredentialingSpecialists,
    loadCredentialingSpecialistsFailure,
    loadCredentialingSpecialistsSuccess,
    loadNotificationCategory,
    loadNotificationCategoryFailure,
    loadNotificationCategorySuccess,
    loadPerDiemRules,
    loadPerDiemRulesFailure,
    loadPerDiemRulesSuccess,
    loadPerDiemSpecialists,
    loadPerDiemSpecialistsFailure,
    loadPerDiemSpecialistsSuccess,
    loadRecruiters,
    loadRecruitersFailure,
    loadRecruitersSuccess,
    loadSpecialists,
    loadSpecialistsFailure,
    loadSpecialistsSuccess,
    loadTravelSpecialists,
    loadTravelSpecialistsFailure,
    loadTravelSpecialistsSuccess,
    loadTypes,
    loadTypesFailure,
    loadTypesSuccess,
    updateAutoAssignSettings,
    updateAutoAssignSettingsFailure,
    updateAutoAssignSettingsSuccess,
    updateCredentialSpecialists,
    updateCredentialSpecialistsFailure,
    updateCredentialSpecialistsSuccess,
    updateCredentialTravelSpecialists,
    updateCredentialTravelSpecialistsFailure,
    updateCredentialTravelSpecialistsSuccess,
    updateNotificationCategory,
    updateNotificationCategoryFailure,
    updateNotificationCategorySuccess,
    updatePerDiemRules,
    updatePerDiemRulesFailure,
    updatePerDiemRulesSuccess,
    updateSpecialists,
    updateSpecialistsFailure,
    updateSpecialistsSuccess
} from 'src/app/credentialing/+store/actions/credentialing.actions';
import { showToasterMessage } from 'src/app/shared/actions/toaster.actions';
import { CredentialingState } from 'src/app/credentialing/+store/state/credentialing.state';
import { AutoAssignService } from 'src/app/credentialing/auto-assign/auto-assign.service';

@Injectable()
export class CredentialingSettingsEffects {
    constructor(
        private readonly _store: Store<CredentialingState>,
        private readonly _actions$: Actions,
        private readonly _credentialingService: CredentialingService,
        private readonly _autoAssignService: AutoAssignService
    ) {}

    loadAutoAssignSettings$ = createEffect(() =>
        this._actions$.pipe(
            ofType(loadAutoAssignSettings),
            switchMap((action) => {
                const reqs = [];

                // Auto assign settings are not returned as an array
                // They are all individually retrieved
                action.settingIds.forEach((id) => {
                    reqs.push(this._credentialingService.getAssignmentSwitch(id));
                });

                return forkJoin(reqs).pipe(
                    map((result: AutoAssignType[]) => loadAutoAssignSettingsSuccess({ settings: result })),
                    catchError((error) => of(loadAutoAssignSettingsFailure({ error })))
                );
            })
        )
    );

    updateAutoAssignSetting$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateAutoAssignSettings),
            switchMap((action) => {
                return this._credentialingService.updateAssignmentSwitch(action.settings).pipe(
                    map(() => {
                        this._store.dispatch(loadAutoAssignSettings({ settingIds: SETTING_IDS }));
                        return updateAutoAssignSettingsSuccess();
                    }),
                    catchError((error) => of(updateAutoAssignSettingsFailure({ error })))
                );
            })
        )
    );

    loadNotificationCategory$ = createEffect(() =>
        this._actions$.pipe(
            ofType(loadNotificationCategory, updateNotificationCategorySuccess),
            switchMap((action) => {
                return this._credentialingService.getNotificationSetting(action.categoryId).pipe(
                    map((result) => loadNotificationCategorySuccess({ notification: result })),
                    catchError((error) => of(loadNotificationCategoryFailure({ error })))
                );
            })
        )
    );

    updateNotificationCategory$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateNotificationCategory),
            switchMap((action) => {
                return this._credentialingService.updateNotificationSetting(action.update).pipe(
                    map(() => updateNotificationCategorySuccess({ categoryId: action.update.notificationCategoryId })),
                    catchError((error) => of(updateNotificationCategoryFailure({ error })))
                );
            })
        )
    );

    loadAgencies$ = createEffect(() =>
        this._actions$.pipe(
            ofType(loadAgencies, updateCredentialSpecialistsSuccess, updateSpecialistsSuccess),
            switchMap((action) => {
                return this._autoAssignService.getUnassignedAgencies().pipe(
                    map((result) => loadAgenciesSuccess({ agencies: result })),
                    catchError((error) => of(loadAgenciesFailure({ error })))
                );
            })
        )
    );

    loadCredentialingSpecialists$ = createEffect(() =>
        this._actions$.pipe(
            ofType(loadCredentialingSpecialists, updateCredentialSpecialistsSuccess, updateSpecialistsSuccess),
            switchMap((action) => {
                return this._autoAssignService.getAgencyUsers().pipe(
                    map((result) =>
                        loadCredentialingSpecialistsSuccess({
                            credentialingSpecialists: result
                        })
                    ),
                    catchError((error) => of(loadCredentialingSpecialistsFailure({ error })))
                );
            })
        )
    );

    updateCredentialingSpecialists$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateCredentialSpecialists),
            switchMap((action) => {
                return this._autoAssignService.saveAgencyUsers(action.payload).pipe(
                    map((result) => updateCredentialSpecialistsSuccess()),
                    catchError((error) => of(updateCredentialSpecialistsFailure({ error })))
                );
            })
        )
    );

    loadRecruiters$ = createEffect(() =>
        this._actions$.pipe(
            ofType(loadRecruiters),
            switchMap((action) => {
                return this._autoAssignService.getUnassignedRecruiters().pipe(
                    map((result) => loadRecruitersSuccess({ recruiters: result })),
                    catchError((error) => of(loadRecruitersFailure({ error })))
                );
            })
        )
    );

    loadTypes$ = createEffect(() =>
        this._actions$.pipe(
            ofType(loadTypes),
            switchMap((action) => {
                return this._autoAssignService.getTypes().pipe(
                    map((result) => loadTypesSuccess({ types: result })),
                    catchError((error) => of(loadTypesFailure({ error })))
                );
            })
        )
    );

    loadSpecialists$ = createEffect(() =>
        this._actions$.pipe(
            ofType(loadSpecialists, updateSpecialistsSuccess),
            switchMap((action) => {
                return this._autoAssignService.getAdminSpecialists().pipe(
                    map((result) => loadSpecialistsSuccess({ adminSpecialists: result })),
                    catchError((error) => of(loadSpecialistsFailure({ error })))
                );
            })
        )
    );

    updateAdminSpecialist$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateSpecialists),
            switchMap((action) => {
                return this._autoAssignService.updateAdminSpecialists(action.adminSpecialists).pipe(
                    map((result) => updateSpecialistsSuccess()),
                    catchError((error) => of(updateSpecialistsFailure({ error })))
                );
            })
        )
    );

    loadTravelCredentialingSpecialists$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateCredentialTravelSpecialistsSuccess, loadTravelSpecialists, updateSpecialistsSuccess),
            switchMap((action) => {
                return this._autoAssignService.getTravelCredentialingSpecialists().pipe(
                    map((result) =>
                        loadTravelSpecialistsSuccess({
                            travelCredentialingSpecialists: result
                        })
                    ),
                    catchError((error) => of(loadTravelSpecialistsFailure({ error })))
                );
            })
        )
    );

    updateTravelCredentialingSpecialists$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateCredentialTravelSpecialists),
            switchMap((action) => {
                return this._autoAssignService.saveTravelUsers(action.payload).pipe(
                    map((result) => updateCredentialTravelSpecialistsSuccess()),
                    catchError((error) => of(updateCredentialTravelSpecialistsFailure({ error })))
                );
            })
        )
    );

    loadRules$ = createEffect(() =>
        this._actions$.pipe(
            ofType(loadPerDiemRules, updatePerDiemRulesSuccess, updateSpecialistsSuccess),
            switchMap((action) => {
                return this._autoAssignService.getPerDiemRules().pipe(
                    map((result) => loadPerDiemRulesSuccess({ perDiemRules: result })),
                    catchError((error) => of(loadPerDiemRulesFailure({ error })))
                );
            })
        )
    );

    updateRules$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updatePerDiemRules),
            switchMap((action) => {
                return this._autoAssignService.updatePerDiemAssigment(action.perDiemRules).pipe(
                    map(() => updatePerDiemRulesSuccess()),
                    catchError((error) => of(updatePerDiemRulesFailure({ error })))
                );
            })
        )
    );

    loadPerDiemSpecialists$ = createEffect(() =>
        this._actions$.pipe(
            ofType(loadPerDiemSpecialists, updateSpecialistsSuccess),
            switchMap((action) => {
                return this._autoAssignService.getPerDiemSpecialists().pipe(
                    map((result) => loadPerDiemSpecialistsSuccess({ perDiemSpecialists: result })),
                    catchError((error) => of(loadPerDiemSpecialistsFailure({ error })))
                );
            })
        )
    );

    showMessage$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateNotificationCategorySuccess, updateAutoAssignSettingsSuccess),
            map(() => showToasterMessage({ message: 'Option is saved' }))
        )
    );

    showMessageUpdatedSuccess$ = createEffect(() =>
        this._actions$.pipe(
            ofType(
                updateCredentialSpecialistsSuccess,
                updateCredentialTravelSpecialistsSuccess,
                updatePerDiemRulesSuccess,
                updateSpecialistsSuccess
            ),
            map(() => showToasterMessage({ message: 'Updated success' }))
        )
    );

    showMessageUpdatedFailed$ = createEffect(() =>
        this._actions$.pipe(
            ofType(
                updateCredentialSpecialistsFailure,
                updatePerDiemRulesFailure,
                updateCredentialTravelSpecialistsFailure,
                updateSpecialistsFailure
            ),
            map(() => showToasterMessage({ message: 'Update failed' }))
        )
    );
}
